import React from 'react';
import Icon from 'components/AppIcon';

const HealthStatusSection = ({ data = {}, onChange, errors = [] }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const healthcareWorkerOptions = [
    { value: 'yes-frontline', label: 'Yes, frontline healthcare worker' },
    { value: 'yes-support', label: 'Yes, healthcare support staff' },
    { value: 'no', label: 'No, not a healthcare worker' }
  ];

  const covidStatusOptions = [
    { value: 'never-had', label: 'Never had COVID-19' },
    { value: 'recovered', label: 'Recovered from COVID-19' },
    { value: 'long-covid', label: 'Experiencing long COVID symptoms' },
    { value: 'currently-positive', label: 'Currently positive for COVID-19' },
    { value: 'unsure', label: 'Unsure/Never tested' }
  ];

  const underlyingConditions = [
    { value: 'diabetes', label: 'Diabetes' },
    { value: 'hypertension', label: 'High blood pressure' },
    { value: 'heart-disease', label: 'Heart disease' },
    { value: 'asthma', label: 'Asthma' },
    { value: 'copd', label: 'COPD or other lung disease' },
    { value: 'autoimmune', label: 'Autoimmune disorder' },
    { value: 'cancer', label: 'Cancer (current or history)' },
    { value: 'mental-health', label: 'Pre-existing mental health condition' },
    { value: 'obesity', label: 'Obesity' },
    { value: 'kidney-disease', label: 'Kidney disease' },
    { value: 'none', label: 'None of the above' }
  ];

  const vaccinationOptions = [
    { value: 'fully-vaccinated', label: 'Fully vaccinated (including boosters)' },
    { value: 'partially-vaccinated', label: 'Partially vaccinated' },
    { value: 'not-vaccinated', label: 'Not vaccinated' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  return (
    <div className="space-y-8">
      {/* Healthcare Worker Status */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Are you a healthcare worker? *
        </label>
        <div className="space-y-3">
          {healthcareWorkerOptions.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.healthcareWorker === option.value
                  ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="healthcareWorker"
                value={option.value}
                checked={data.healthcareWorker === option.value}
                onChange={(e) => handleInputChange('healthcareWorker', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.healthcareWorker === option.value
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {data.healthcareWorker === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* COVID-19 Status */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          COVID-19 Experience
        </label>
        <div className="space-y-3">
          {covidStatusOptions.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.covidStatus === option.value
                  ? 'border-secondary bg-secondary-50 text-secondary-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="covidStatus"
                value={option.value}
                checked={data.covidStatus === option.value}
                onChange={(e) => handleInputChange('covidStatus', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.covidStatus === option.value
                  ? 'border-secondary bg-secondary' :'border-border'
                }
              `}>
                {data.covidStatus === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Vaccination Status */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Vaccination Status
        </label>
        <div className="space-y-3">
          {vaccinationOptions.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.vaccinationStatus === option.value
                  ? 'border-accent bg-accent-50 text-accent-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="vaccinationStatus"
                value={option.value}
                checked={data.vaccinationStatus === option.value}
                onChange={(e) => handleInputChange('vaccinationStatus', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.vaccinationStatus === option.value
                  ? 'border-accent bg-accent' :'border-border'
                }
              `}>
                {data.vaccinationStatus === option.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Underlying Health Conditions */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Do you have any of the following underlying health conditions?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {underlyingConditions.map((condition) => (
            <label
              key={condition.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.underlyingConditions || []).includes(condition.value)}
                onChange={(e) => handleMultiSelectChange('underlyingConditions', condition.value, e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{condition.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Health Information */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Additional Health Information (Optional)
        </label>
        <textarea
          value={data.additionalHealthInfo || ''}
          onChange={(e) => handleInputChange('additionalHealthInfo', e.target.value)}
          placeholder="Please share any other relevant health information that might help us provide better recommendations..."
          rows={4}
          className="form-input resize-none"
        />
        <p className="mt-1 text-xs text-text-secondary">
          This information will help us provide more personalized recommendations
        </p>
      </div>

      {/* Privacy Notice */}
      <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} color="#2563EB" className="mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-primary-600 mb-1">
              Privacy & Confidentiality
            </h3>
            <p className="text-sm text-primary-600">
              All health information you provide is encrypted and stored securely. This data is used solely for generating your personalized wellness recommendations and is never shared with third parties without your explicit consent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthStatusSection;